import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/API";

/* get the group list */
export const reportGroupPost = createAsyncThunk(
    "reportGroupPost",
    async ({ groupId, postId, form_data }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(
                `/group/report/group/${groupId}/post/${postId}/`,
                form_data,
                {
                    headers: {
                        "content-type": "multipart/form-data",
                    },
                }
            );
            const result = response.data;
            return result;
        } catch (error) {
            console.error("Error:", error);
            // Extract error message from response
            if (error.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ detail: "An error occurred while reporting the post." });
        }
    }
);

export const groupPostReport = createAsyncThunk(
    "groupPostReport",
    async ({ groupId, postId, form_data }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`/group_reports/posts/${groupId}/${postId}/`, form_data);
            const result = response.data;
            return result;
        } catch (error) {
            console.error("Error:", error);
            if (error.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ detail: "An error occurred while reporting the post." });
        }
    }
);

export const getAllGroupPostReports = createAsyncThunk(
    "getAllGroupPostReports",
    async (groupId, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/group_reports/reported-posts/${groupId}/`);
            const result = response.data;
            return result;
        } catch (error) {
            console.error("Error:", error);
            if (error.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ detail: "An error occurred while fetching group post reports." });
        }
    }
);

export const getFilteredGroupPostReports = createAsyncThunk(
    "getFilteredGroupPostReports",
    async ({ groupId, filter }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(`/group_reports/reported-posts/${groupId}/?time_filter=${filter}`);
            const result = response.data;
            return result;
        } catch (error) {
            console.error("Error:", error);
            if (error.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ detail: "An error occurred while fetching filtered group post reports." });
        }
    }
);

export const deleteGroupPostReport = createAsyncThunk(
    "deleteGroupPostReport",
    async ({ groupId, postId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.delete(`/group_reports/delete-report/${groupId}/${postId}/`);
            const result = response.data;
            return result;
        } catch (error) {
            console.error("Error:", error);
            if (error.response?.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ detail: "An error occurred while deleting the group post report." });
        }
    }
);

export const reportBanUnbanUser = createAsyncThunk("reportBanUnbanUser", async ({ groupId, userId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/group_reports/ban-unban-user/${groupId}/${userId}/`);
        return response.data;
    } catch (error) {
        console.error("Error:", error.response?.data);
        return rejectWithValue(error.response?.data || { detail: "An error occurred." });
    }
});

export const getAllBannedUsers = createAsyncThunk("getAllBannedUsers", async (groupId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/group_reports/banned-users/${groupId}/`);
        return response.data;
    } catch (error) {
        console.error("Error:", error.response?.data);
        return rejectWithValue(error.response?.data || { detail: "An error occurred." });
    }
});

export const postWarning = createAsyncThunk(
    "postWarning",
    async ({ groupId, postId }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`group_reports/warn-post/${groupId}/${postId}/`, {});
            return response.data;
        } catch (error) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data);
            }
            return rejectWithValue({ non_field_errors: ["Something went wrong."] });
        }
    }
);

export const GroupPostReportSlice = createSlice({
    name: "GroupPostReportSlice",
    initialState: {
        data: [],
        loading: false,
        error: null,
    },
    extraReducers: (builder) => {

        // get group list action promises
        builder.addCase(reportGroupPost.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(reportGroupPost.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = false;
        });
        builder.addCase(reportGroupPost.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // get group list action promises
        builder.addCase(getAllGroupPostReports.pending, (state) => {
            state.loading = true;
            state.data = [];
        }
        );
        builder.addCase(getAllGroupPostReports.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = false;
        }
        );
        builder.addCase(getAllGroupPostReports.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });


        // get filtered group post reports action promises
        builder.addCase(getAllBannedUsers.pending, (state) => {
            state.loading = true;
            state.data = [];
        }
        );
        builder.addCase(getAllBannedUsers.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = false;
        }
        );
        builder.addCase(getAllBannedUsers.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });

        // get filtered group post reports action promises
        builder.addCase(getFilteredGroupPostReports.pending, (state) => {
            state.loading = true;
            state.data = [];
        }
        );
        builder.addCase(getFilteredGroupPostReports.fulfilled, (state, action) => {
            state.data = action.payload;
            state.loading = false;
        }
        );
        builder.addCase(getFilteredGroupPostReports.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        });
    },
});

export default GroupPostReportSlice.reducer;
